import { useCustomsDocsApi } from "@/Hooks/useCustomsDocsApi";
import { CustomsDocsUploadMode } from "@/Apis/CustomsDocsUploadMode";
import type { UUID } from "@/Shared/types";
import { CustomDocsSubmitSection } from "@/Shared/CustomDocs/CustomComponents/CustomDocsSubmitSection";
import { useCustomDocsDropzone } from "@/Shared/CustomDocs/Hooks/useCustomDocsDropzone";
import { useState } from "react";
import { Button } from "@freightsimple/ui-core/Controls/Button";

interface CustomsDocsSectionProps {
  shipmentId: UUID;
  onUploaded: () => void;
}

export function ViewShipmentsCustomsDocsSection(
  props: CustomsDocsSectionProps,
) {
  const customsDocsApi = useCustomsDocsApi();

  const [loading, setLoading] = useState(false);
  const [loadingSuccess, setLoadingSuccess] = useState(false);
  const [loadingFailure, setLoadingFailure] = useState(false);

  const { files, setFiles, dropzoneState } = useCustomDocsDropzone();

  async function onUpload() {
    setLoadingSuccess(false);
    setLoadingFailure(false);
    setLoading(true);
    try {
      await customsDocsApi.upload(
        props.shipmentId,
        CustomsDocsUploadMode.Initial,
        files,
      );
      setLoadingSuccess(true);
      setTimeout(function () {
        props.onUploaded();
      }, 1000);
    } catch (e) {
      console.error(`Failure uploading customs docs`, { e });
      setLoadingFailure(true);
    } finally {
      setLoading(false);
    }
  }

  function acceptedFilesCount() {
    if (files.length === 0) {
      return "";
    }

    if (files.length === 1) {
      return " (1 file)";
    }

    if (files.length > 1) {
      return ` (${files.length} files)`;
    }

    // should not be here
    throw new Error("Invalid files count");
  }

  return (
    <div id="next-steps-section" style={{ width: "100%" }}>
      <div className="relative mb-12 inline-block rounded-[3px] border border-border-strong px-10 pt-6 pb-10 [box-shadow:var(--color-shadow-ambient)_0px_3px_12px]">
        <div className="flex flex-col items-center">
          <CustomDocsSubmitSection
            setFiles={setFiles}
            dropzoneState={dropzoneState}
            files={files}
            title={"Next: Upload Customs Documents"}
            subtitle={
              <>
                <p>
                  FreightSimple will send your customs documents to the carrier
                  to ensure there are no delays. These <strong>MUST</strong> be
                  submitted before pickup as some carriers will not pick up the
                  freight until they receive these documents.
                </p>
                <p>
                  If you're not sure what documents you need, or to learn more
                  please see our{" "}
                  <Button
                    size="link"
                    inheritFont
                    href="https://help.freightsimple.com/using-freightsimple/booking-your-shipment/uploading-your-customs-documents"
                  >
                    Help Center
                  </Button>
                </p>
              </>
            }
          />
          {files.length !== 0 && (
            <Button
              primary
              size="large"
              onClick={onUpload}
              loadingSuccess={loadingSuccess}
              loading={loading}
              loadingFailure={loadingFailure}
              disabled={loading || loadingSuccess}
              disabledMessage="Upload is in progress"
            >
              Upload Customs Docs {acceptedFilesCount()}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
